import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  Flex,
  useToast,
  Icon,
  Select,
  Spinner,
  Textarea,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { FiBriefcase, FiMapPin, FiHome, FiPlusCircle } from "react-icons/fi";
import OptionsService from "../Enterprise/OptionsService";
import CompanyAddressService from "../Enterprise/CompanyAddressService";
import ModernPageShell from "../../components/layout/ModernPageShell";
import { getModernUi } from "../../components/layout/modernUi";

export default function Enterprise({ modern = false }) {
  const ui = getModernUi(modern);
  const toast = useToast();

  // --- Estados para Empresas (Options com type='Company') ---
  const [empresasOptions, setEmpresasOptions] = useState([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(true);
  const [errorEmpresas, setErrorEmpresas] = useState(null);

  const [novaEmpresaForm, setNovaEmpresaForm] = useState({
    nome: "",
  });

  const [novaFilialForm, setNovaFilialForm] = useState({
    empresaId: "",
    unityName: "",
    additionalAddressDetails: "",
  });

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoadingEmpresas(true);
        setErrorEmpresas(null);

        const data = await OptionsService.getOptionsByType("Company");
        setEmpresasOptions(data);
      } catch (err) {
        setErrorEmpresas("Não foi possível carregar as empresas.");
        toast({
          title: "Erro ao carregar empresas.",
          description:
            "Verifique sua conexão com o backend e se há empresas cadastradas.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        console.error("Erro no useEffect de fetchCompanies:", err);
      } finally {
        setLoadingEmpresas(false);
      }
    };

    fetchCompanies();
  }, []);

  // --- Handlers para o formulário de Cadastro de Nova Empresa ---
  const handleChangeNovaEmpresa = (e) => {
    const { name, value } = e.target;
    setNovaEmpresaForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitNovaEmpresa = async () => {
    if (!novaEmpresaForm.nome) {
      toast({
        title: "O nome da empresa é obrigatório.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const optionData = {
        description: novaEmpresaForm.nome,
        type: "Company",
      };
      const createdOption = await OptionsService.createOption(optionData);

      setEmpresasOptions((prev) => [...prev, createdOption]);

      toast({
        title: "Empresa cadastrada com sucesso!",
        description: `ID: ${createdOption.options_id}, Nome: ${createdOption.description}`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setNovaEmpresaForm({ nome: "" });
    } catch (error) {
      toast({
        title: "Erro ao cadastrar empresa.",
        description:
          error.response?.data?.message || "Tente novamente mais tarde.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      console.error("Erro ao cadastrar empresa:", error);
    }
  };

  // --- Handlers para o formulário de Cadastro de Nova Filial ---
  const handleChangeNovaFilial = (e) => {
    const { name, value } = e.target;
    setNovaFilialForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitNovaFilial = async () => {
    if (
      !novaFilialForm.empresaId ||
      !novaFilialForm.unityName ||
      !novaFilialForm.additionalAddressDetails
    ) {
      toast({
        title: "Preencha todos os campos obrigatórios da filial.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const companyAddressData = {
        unityName: novaFilialForm.unityName,

        additionalAddressDetails: novaFilialForm.additionalAddressDetails,

        company: {
          optionsId: novaFilialForm.empresaId,
        },
      };

      const createdCompanyAddress =
        await CompanyAddressService.createCompanyAddress(companyAddressData);

      toast({
        title: "Filial cadastrada com sucesso!",
        description: `Filial: ${createdCompanyAddress.unityName}, ID: ${createdCompanyAddress.companyAddressId}`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setNovaFilialForm({
        empresaId: "",
        unityName: "",
        additionalAddressDetails: "",
      });
    } catch (error) {
      toast({
        title: "Erro ao cadastrar filial.",
        description:
          error.response?.data?.message ||
          "Ocorreu um erro desconhecido. Tente novamente mais tarde.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      console.error("Erro ao cadastrar filial:", error.response?.data || error);
    }
  };

  const pageContent = (
    <>
      {!modern && (
        <Heading
          mb={6}
          fontSize="2xl"
          color="#C1272D"
          borderBottom="2px solid #625752"
          pb={2}
        >
          Gerenciamento de Empresas e Filiais - SENCA
        </Heading>
      )}

      <Box mb={10} {...ui.card}>
        <Heading
          size="md"
          mb={4}
          color="#3A3A3A"
          display="flex"
          alignItems="center"
          gap={2}
        >
          <Icon as={FiBriefcase} /> Cadastrar Nova Empresa
        </Heading>
        <Flex direction="column" gap={5}>
          <FormControl isRequired>
            <FormLabel
              color="#3A3A3A"
              display="flex"
              alignItems="center"
              gap={2}
            >
              Nome da Empresa
            </FormLabel>
            <Input
              placeholder="Digite o nome da empresa"
              name="nome"
              value={novaEmpresaForm.nome}
              onChange={handleChangeNovaEmpresa}
              bg="white"
              borderColor="#625752"
              focusBorderColor="#C1272D"
            />
          </FormControl>
          <Flex justify="center" gap={4} mt={3}>
            <Button
              bg="#C1272D"
              color="white"
              _hover={{ bg: "#a81f23" }}
              px={8}
              onClick={handleSubmitNovaEmpresa}
            >
              Cadastrar Empresa
            </Button>
            <Button
              variant="outline"
              borderColor="#625752"
              color="#625752"
              _hover={{ bg: "#f4f4f4" }}
              px={8}
              onClick={() => setNovaEmpresaForm({ nome: "" })}
            >
              Limpar
            </Button>
          </Flex>
        </Flex>
      </Box>

      {/* --- Separador Visual --- */}
      <Box my={10} borderBottom="1px dashed #DDD" />

      {/* --- Seção de Cadastro de Filial (CompanyAddress) --- */}
      <Box {...ui.card}>
        <Heading
          size="md"
          mb={4}
          color="#3A3A3A"
          display="flex"
          alignItems="center"
          gap={2}
        >
          <Icon as={FiPlusCircle} /> Cadastrar Filial
        </Heading>
        <Flex direction="column" gap={5}>
          <FormControl isRequired>
            <FormLabel
              color="#3A3A3A"
              display="flex"
              alignItems="center"
              gap={2}
            >
              <Icon as={FiBriefcase} /> Selecione a Empresa
            </FormLabel>
            {loadingEmpresas ? (
              <Flex justify="center" align="center" height="50px">
                <Spinner size="md" color="#C1272D" />
                <Box ml={3}>Carregando empresas...</Box>
              </Flex>
            ) : errorEmpresas ? (
              <Box color="red.500">{errorEmpresas}</Box>
            ) : (
              <Select
                placeholder="Selecione a empresa"
                name="empresaId"
                value={novaFilialForm.empresaId}
                onChange={handleChangeNovaFilial}
                bg="white"
                borderColor="#625752"
                focusBorderColor="#C1272D"
              >
                {empresasOptions.length === 0 && (
                  <option disabled>Nenhuma empresa cadastrada.</option>
                )}

                {empresasOptions.map((emp) => (
                  <option key={emp.optionsId} value={emp.optionsId}>
                    {emp.description}
                  </option>
                ))}
              </Select>
            )}
          </FormControl>

          <FormControl isRequired>
            <FormLabel
              color="#3A3A3A"
              display="flex"
              alignItems="center"
              gap={2}
            >
              <Icon as={FiHome} /> Nome da Filial (Nome da Unidade)
            </FormLabel>
            <Input
              placeholder="Ex: Unidade SP, Matriz, Filial Centro"
              name="unityName"
              value={novaFilialForm.unityName}
              onChange={handleChangeNovaFilial}
              bg="white"
              borderColor="#625752"
              focusBorderColor="#C1272D"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel
              color="#3A3A3A"
              display="flex"
              alignItems="center"
              gap={2}
            >
              <Icon as={FiMapPin} /> Endereço Completo (Rua, Número, Bairro,
              Cidade/UF)
            </FormLabel>
            <Textarea
              placeholder="Rua, número, bairro, cidade/UF da filial"
              name="additionalAddressDetails"
              value={novaFilialForm.additionalAddressDetails}
              onChange={handleChangeNovaFilial}
              bg="white"
              borderColor="#625752"
              focusBorderColor="#C1272D"
            />
          </FormControl>

          <Flex justify="center" gap={4} mt={3}>
            <Button
              bg="#C1272D"
              color="white"
              _hover={{ bg: "#a81f23" }}
              px={8}
              onClick={handleSubmitNovaFilial}
            >
              Cadastrar Filial
            </Button>
            <Button
              variant="outline"
              borderColor="#625752"
              color="#625752"
              _hover={{ bg: "#f4f4f4" }}
              px={8}
              onClick={() =>
                setNovaFilialForm({
                  empresaId: "",
                  unityName: "",
                  additionalAddressDetails: "",
                })
              }
            >
              Limpar
            </Button>
          </Flex>
        </Flex>
      </Box>
    </>
  );

  if (modern) {
    return (
      <ModernPageShell
        title="Cadastro de empresas"
        subtitle="Cadastre empresas e filiais vinculadas ao sistema."
        eyebrow="Empresas"
        centered
      >
        {pageContent}
      </ModernPageShell>
    );
  }

  return (
    <Box
      maxW="700px"
      mx="auto"
      mt={10}
      p={10}
      bg="white"
      borderRadius="2xl"
      boxShadow="lg"
      border="1px solid"
      borderColor="#DDD"
    >
      {pageContent}
    </Box>
  );
}
